import { useCallback, useEffect, useRef, useState } from 'react'
import { sessionStore } from '@features/auth/services/sessionStore'
import {
  deleteAllNotifications,
  deleteNotification,
  listNotifications,
  markNotificationsRead,
} from '../api/notificationsApi'
import type { NotificationWire } from '../types'

/**
 * 通知センター用フック (T-H1)。
 *
 * 設計判断:
 *   - 初期ロード + popover を開いたタイミングで再 fetch (push なし。Phase 6 後半で
 *     SSE / websocket への昇格を検討)。
 *   - mark-read は楽観更新: API 呼出前に local の `readAt` を埋め、API 失敗時に rollback。
 *   - sessionStore が UNAUTH の間は fetch しない (401 retry を避ける)。
 */

export interface UseNotifications {
  items: readonly NotificationWire[]
  unreadCount: number
  loading: boolean
  refresh: () => Promise<void>
  markAllRead: () => Promise<void>
  markRead: (id: string) => Promise<void>
  remove: (id: string) => Promise<void>
  clearAll: () => Promise<void>
}

export const useNotifications = (): UseNotifications => {
  const [items, setItems] = useState<readonly NotificationWire[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const mountedRef = useRef(true)

  const refresh = useCallback(async (): Promise<void> => {
    if (sessionStore.getState().kind !== 'unlocked') return
    setLoading(true)
    try {
      const res = await listNotifications()

      if (!mountedRef.current) return
      setItems(res.items)
      setUnreadCount(res.unreadCount)
    } catch {
      // 通知 fetch 失敗はサイレント (Topbar の bell に表示されるだけの非クリティカル機能)
    } finally {
      if (mountedRef.current) setLoading(false)
    }
  }, [])

  useEffect(() => {
    mountedRef.current = true
    void refresh()

    // sessionStore が unlocked になった瞬間も再 fetch
    const unsubscribe = sessionStore.subscribe(() => {
      if (sessionStore.getState().kind === 'unlocked') {
        void refresh()
      } else {
        // 未認証になったら表示を空に戻す
        setItems([])
        setUnreadCount(0)
      }
    })

    return () => {
      mountedRef.current = false
      unsubscribe()
    }
  }, [refresh])

  const markAllRead = useCallback(async (): Promise<void> => {
    if (unreadCount === 0) return
    const prevItems = items
    const prevUnread = unreadCount
    const nowIso = new Date().toISOString()

    // 楽観更新
    setItems(prev => prev.map(it => (it.readAt ? it : { ...it, readAt: nowIso })))
    setUnreadCount(0)

    try {
      const res = await markNotificationsRead(null)
      if (!mountedRef.current) return
      setUnreadCount(res.unreadCount)
    } catch {
      if (!mountedRef.current) return
      setItems(prevItems)
      setUnreadCount(prevUnread)
    }
  }, [items, unreadCount])

  const markRead = useCallback(
    async (id: string): Promise<void> => {
      const target = items.find(it => it.id === id)
      if (!target || target.readAt) return
      const prevItems = items
      const prevUnread = unreadCount
      const nowIso = new Date().toISOString()

      setItems(prev => prev.map(it => (it.id === id ? { ...it, readAt: nowIso } : it)))
      setUnreadCount(c => Math.max(0, c - 1))

      try {
        const res = await markNotificationsRead([id])
        if (!mountedRef.current) return
        setUnreadCount(res.unreadCount)
      } catch {
        if (!mountedRef.current) return
        setItems(prevItems)
        setUnreadCount(prevUnread)
      }
    },
    [items, unreadCount],
  )

  const remove = useCallback(
    async (id: string): Promise<void> => {
      const prevItems = items
      const prevUnread = unreadCount

      // 楽観削除
      setItems(prev => prev.filter(it => it.id !== id))

      try {
        const res = await deleteNotification(id)
        if (!mountedRef.current) return
        setUnreadCount(res.unreadCount)
      } catch {
        if (!mountedRef.current) return
        setItems(prevItems)
        setUnreadCount(prevUnread)
      }
    },
    [items, unreadCount],
  )

  const clearAll = useCallback(async (): Promise<void> => {
    if (items.length === 0) return
    const prevItems = items
    const prevUnread = unreadCount

    setItems([])
    setUnreadCount(0)

    try {
      await deleteAllNotifications()
    } catch {
      if (!mountedRef.current) return
      setItems(prevItems)
      setUnreadCount(prevUnread)
    }
  }, [items, unreadCount])

  return { items, unreadCount, loading, refresh, markAllRead, markRead, remove, clearAll }
}
